/**
 * Unsubscribe confirmation page for a self-service feed.
 *
 * Loading this page must NEVER unsubscribe anyone. Mail scanners and link
 * prefetchers open every URL in an inbound message, so the opt-out only happens
 * when the visitor presses the button — which issues the POST.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../app/http-common';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import './FeedBuilder.css';

export default function UnsubscribePage() {
  const { token } = useParams();
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getUnsubscribeTarget(token);
        if (!cancelled) {
          setTarget(res);
          if (res.alreadyUnsubscribed) setDone({ feedName: res.feedName, already: true });
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'This unsubscribe link is not valid.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const confirm = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.confirmUnsubscribe(token);
      setDone({ feedName: res.feedName, already: false });
    } catch (err) {
      setError(err.response?.data?.error || 'We could not process that. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [token]);

  return (
    <div className="sfb-page">
      <div className="sfb-top-bar">
        <div className="sfb-top-bar-inner">
          <a href="/" aria-label="Alchemiq">
            <img
              src="/textOnlyLogoOrr-01.png"
              alt="Alchemiq"
              className="sfb-logo"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </a>
          <span className="sfb-product-name">News Feed Builder</span>
        </div>
      </div>

      <main className="sfb-main">
        {loading && <div className="sfb-loading"><span className="sfb-spinner" />Loading…</div>}

        {!loading && error && !done && (
          <div className="sfb-notice">
            <InfoOutlinedIcon style={{ fontSize: 17, flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}

        {!loading && done && (
          <>
            <p className="sfb-eyebrow" style={{ marginBottom: 10 }}>Alchemiq global intelligence</p>
            <h1 className="sfb-title">
              {done.already ? 'Already unsubscribed' : 'You have been unsubscribed'}
            </h1>
            <div className="sfb-success-banner">
              <CheckCircleOutlineIcon style={{ fontSize: 19, flexShrink: 0 }} />
              <span>
                {done.already
                  ? 'You are already unsubscribed from this feed.'
                  : 'You will not receive this feed again.'}
              </span>
            </div>
            <p className="sfb-lede" style={{ marginBottom: 0 }}>
              {done.feedName ? <>Feed: <strong style={{ fontWeight: 600 }}>{done.feedName}</strong><br /></> : null}
              Changed your mind? Reply to any earlier email from us and we will
              turn it back on.
            </p>
          </>
        )}

        {!loading && !done && target && (
          <>
            <p className="sfb-eyebrow" style={{ marginBottom: 10 }}>Alchemiq global intelligence</p>
            <h1 className="sfb-title">Stop receiving this feed?</h1>
            <p className="sfb-lede">
              You are about to unsubscribe{target.recipientHint ? ` ${target.recipientHint}` : ''} from{' '}
              <strong style={{ fontWeight: 600 }}>{target.feedName}</strong>. We will stop
              sending it, and the feed will stop running.
            </p>
            <div className="sfb-actions">
              <a className="sfb-btn sfb-btn--secondary" href="/" style={{ textDecoration: 'none' }}>
                Keep receiving it
              </a>
              <button
                type="button"
                className="sfb-btn sfb-btn--primary"
                onClick={confirm}
                disabled={submitting}
              >
                {submitting ? 'Unsubscribing…' : 'Unsubscribe'}
              </button>
            </div>
            {error && (
              <div className="sfb-notice" style={{ marginTop: 20, marginBottom: 0 }}>
                <InfoOutlinedIcon style={{ fontSize: 17, flexShrink: 0, marginTop: 1 }} />
                <span>{error}</span>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
